import React, { useState } from 'react'
import { Link, usePage, router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { ThemeToggle } from '@/components/ThemeToggle'
import { Menu, Package, Settings, LogOut, LayoutDashboard } from 'lucide-react'
import { Logo } from '@/components/Logo'

type Props = {
  children: React.ReactNode
}

type PageProps = {
  currentUser: { id: number; email: string; username: string } | null
}

export default function AppLayout({ children }: Props) {
  const { currentUser } = usePage().props as PageProps
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const handleLogout = () => {
    router.delete('/logout')
  }

  const NavLinks = () => (
    <>
      {currentUser && (
        <>
          <Link href="/dashboard" className="flex items-center gap-2 text-sm text-foreground hover:text-primary transition-colors">
            <LayoutDashboard className="h-4 w-4" />
            ダッシュボード
          </Link>
          <Link href="/repositories" className="flex items-center gap-2 text-sm text-foreground hover:text-primary transition-colors">
            <Package className="h-4 w-4" />
            リポジトリ
          </Link>
          <Link href="/settings" className="flex items-center gap-2 text-sm text-foreground hover:text-primary transition-colors">
            <Settings className="h-4 w-4" />
            設定
          </Link>
        </>
      )}
    </>
  )

  return (
    <div className="min-h-screen bg-muted/20 dark:bg-background dot-pattern">
      <a href="#main-content" className="skip-link">
        メインコンテンツへスキップ
      </a>

      <header className="sticky top-0 z-50 border-b border-border/40 bg-background/80 dark:bg-background/95 backdrop-blur-xl supports-[backdrop-filter]:bg-background/60 shadow-sm">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link href="/" className="text-xl text-foreground hover:opacity-80 transition-opacity">
            <Logo />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            <NavLinks />
          </nav>

          <div className="flex items-center gap-2">
            <ThemeToggle />

            {currentUser ? (
              <>
                {/* Desktop User Menu */}
                <div className="hidden md:block">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline">{currentUser.username}</Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem asChild>
                        <Link href="/dashboard">ダッシュボード</Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link href="/settings">設定</Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleLogout}>
                        <LogOut className="mr-2 h-4 w-4" />
                        ログアウト
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Mobile Menu */}
                <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                  <SheetTrigger asChild className="md:hidden">
                    <Button variant="ghost" size="icon" aria-label="メニューを開く">
                      <Menu className="h-5 w-5" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="right">
                    <SheetHeader>
                      <SheetTitle>{currentUser.username}</SheetTitle>
                    </SheetHeader>
                    <nav className="flex flex-col gap-4 mt-6">
                      <NavLinks />
                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-2 text-sm text-destructive"
                      >
                        <LogOut className="h-4 w-4" />
                        ログアウト
                      </button>
                    </nav>
                  </SheetContent>
                </Sheet>
              </>
            ) : (
              <>
                <Link href="/login" className="hidden sm:block">
                  <Button variant="ghost">ログイン</Button>
                </Link>
                <Link href="/signup">
                  <Button>新規登録</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      <main id="main-content" className="container mx-auto px-4 py-8">
        {children}
      </main>

      {/* Footer */}
      <footer className="border-t border-primary/10 mt-auto bg-background">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              &copy; {new Date().getFullYear()} konreji. All rights reserved.
            </p>
            <nav className="flex gap-4 text-sm text-muted-foreground">
              <Link href="/terms" className="hover:underline">
                利用規約
              </Link>
              <Link href="/privacy" className="hover:underline">
                プライバシーポリシー
              </Link>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  )
}
